import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'
import ApiClient from '@src/lib/api_client'

import Checkup, { CheckupState } from '@src/data/checkup'
import { Button } from '@src/elements/button/button'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { API } from '@src/lib/api_path'
import { snakecaseKeys, track } from '@src/util/functions'
import PlanCheckup from '@src/routes/members/components/checkups/plan-checkup'
import { TextCell } from '@src/components/table-list/cells'
import { CheckupOptionsCell } from '@src/routes/members/components/checkups/options-cell'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { StatusCell } from '@src/routes/members/components/checkups/status-cell'
import { CheckupsFilter } from '@src/routes/members/components/checkups/filter'
import { CheckupsList } from '@src/routes/members/components/checkups/list'
import { convertToYYYYMMDD } from '@src/util/date-format'

import './checkups.sass'
import EventBus from '@src/lib/event_bus'
import { CheckupsDeviceFilter } from '@src/routes/members/components/checkups/device-filter'

type CheckupsResponse = { checkups: Checkup[] }
type CheckupResponse = { checkup: Checkup }

@Component
export class Checkups extends Vue {

  private checkups: Checkup[] = []

  private apiClient: ApiClient = new ApiClient()
  private loading: boolean = true

  private isPlanCheckupVisible: boolean = false

  constructor () {
    super()
    track('/members/checkups')
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.DETAIL.TABS.CHECKUPS')}`
  }

  showHidePlanCheckup () {
    this.isPlanCheckupVisible = !this.isPlanCheckupVisible
  }

  planCheckup (date: string) {
    // post empty checkup with PLANNED status
    const newCheckup = snakecaseKeys(this.generateCheckup(CheckupState.PLANNED, date))
    return this.apiClient.post<CheckupResponse>(API.TRAINERS_API.CHECKUPS.replace('{memberId}', this.$route.params.id), { checkup: newCheckup })
      .then((response: CheckupResponse) => {
        if (response.checkup) {
          this.showHidePlanCheckup()
          return this.loadCheckups()
        }
      })
      .catch((error) => console.log('Error - save checkup', error))
  }

  startCheckup () {
    // post empty checkup with IN_PROGRESS status
    const newCheckup = snakecaseKeys(this.generateCheckup(CheckupState.IN_PROGRESS))
    return this.apiClient.post<CheckupResponse>(API.TRAINERS_API.CHECKUPS.replace('{memberId}', this.$route.params.id), { checkup: newCheckup })
      .then((response: CheckupResponse) => {
        if (response.checkup) {
          this.$store.dispatch('setCheckup', new Checkup(camelcaseKeys(response.checkup, { deep: true })))
          this.$router.push(`/trainers/checkup/${this.$route.params.id}/${response.checkup.id}`)
        }
      })
      .catch((error) => console.log('Error - save checkup', error))
  }

  generateCheckup (state: string, date?: string) {
    return new Checkup({
      plannedDate: date ? convertToYYYYMMDD(date) : convertToYYYYMMDD(new Date()),
      state: state
    })
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <Loader />
    }

    if (this.checkups.length === 0) {
      return <ErrorScreen message={this.$t('PAGE.MEMBERS.CHECKUPS.EMPTY_LIST')}>
        <Button ref='startBtn' class='start-btn' label={this.$t('PAGE.MEMBERS.CHECKUPS.START_CHECKUP')} click={this.startCheckup} type='remove' />
      </ErrorScreen>
    }

    return <div class='member-checkups-wrapper scroll-content'>
      <div class='row'>
        <CheckupsFilter />
        <CheckupsDeviceFilter />
        <div class='btns'>
          <div class='plan-checkup'>
            <Button ref='planBtn' class='plan-btn' label={this.$t('PAGE.MEMBERS.CHECKUPS.PLAN_CHECKUP')} click={this.showHidePlanCheckup} type='outline' />
            {this.isPlanCheckupVisible ?
              <PlanCheckup closeCallback={this.showHidePlanCheckup} chooseCallback={this.planCheckup} /> : null}
          </div>

          <Button ref='startBtn' class='start-btn' label={this.$t('PAGE.MEMBERS.CHECKUPS.START_CHECKUP')} click={this.startCheckup} type='elevated' />
        </div>
      </div>

      <CheckupsList checkups={this.checkups} cells={{
        date: TextCell,
        status: StatusCell,
        options: CheckupOptionsCell
      }} />
    </div>
  }

  created () {
    EventBus.on('reloadCheckups', this.loadCheckups)

    return this.loadCheckups()
  }

  beforeDestroy () {
    EventBus.off('reloadCheckups', this.loadCheckups)
  }

  private loadCheckups () {
    return this.apiClient.get<CheckupsResponse>(API.TRAINERS_API.CHECKUPS.replace('{memberId}', this.$route.params.id))
      .then((response: CheckupsResponse) => {
        this.checkups = response.checkups.map((checkup) => new Checkup(camelcaseKeys(checkup)))
          .sort((a, b) => {
            const firstElementDate: Date = a.completedDate ? new Date(a.completedDate) : new Date(a.plannedDate)
            const secondElementDate: Date = b.completedDate ? new Date(b.completedDate) : new Date(b.plannedDate)
            if (firstElementDate.getTime() < secondElementDate.getTime()) return 1
            if (firstElementDate.getTime() > secondElementDate.getTime()) return -1
            return 0
          })
      })
      .catch(error => console.error('failed to retrieve checkup list', error))
      .finally(() => this.loading = false)
  }
}
